import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI, rolesAPI } from '../services/api';
import { disconnectSocket } from '../services/socket';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [permissions, setPermissions] = useState({ allowed_pages: [], all_pages: [] });

  // Check if user is logged in on mount
  useEffect(() => {
    checkAuth();
  }, []);

  const fetchPermissions = async () => {
    try {
      const response = await rolesAPI.getMyPermissions();
      const data = response.data?.data;
      setPermissions({
        role_name: data?.role_name,
        allowed_pages: data?.allowed_pages || [],
        all_pages: data?.all_pages || []
      });
    } catch (error) {
      console.error('Error fetching permissions:', error);
      setPermissions({ allowed_pages: [], all_pages: [] });
    }
  };

  const checkAuth = async () => {
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');

    if (token && savedUser) {
      try {
        // Verify token is still valid
        const response = await authAPI.getProfile();
        const profileData = response.data.data;
        // getProfile returns { user, tenant, subscription }
        const rawUser = profileData?.user || profileData;

        // Extract role name from role object if needed
        const processedUser = {
          ...rawUser,
          role: rawUser.role_id?.name || rawUser.role || 'user',
          branch_id: rawUser.branch_id?._id || rawUser.branch_id || null,
          branch_name: rawUser.branch_id?.branch_name || null,
        };

        setUser(processedUser);
        setIsAuthenticated(true);
        localStorage.setItem('user', JSON.stringify(processedUser));

        // Save tenant & subscription if available
        if (profileData?.tenant) localStorage.setItem('tenant', JSON.stringify(profileData.tenant));
        if (profileData?.subscription) localStorage.setItem('subscription', JSON.stringify(profileData.subscription));

        // Skip permissions fetch when the account hasn't been activated yet
        // — every tenant-scoped endpoint (including /roles/my-permissions)
        // sits behind `requireActiveAccount` and would 402. The axios
        // interceptor would then hard-redirect to /subscription-expired
        // mid-bootstrap, which is exactly the loop users reported. The
        // onboarding UI does not need permissions to render.
        const accountStatus = profileData?.tenant?.accountStatus;
        const isActive = !accountStatus || accountStatus === 'active';
        if (isActive) {
          await fetchPermissions();
        } else {
          setPermissions({ allowed_pages: [], all_pages: [] });
        }
      } catch (error) {
        // Token expired or invalid
        logout();
      }
    }
    setLoading(false);
  };

  const login = async (email, password) => {
    try {
      // Send tenant slug if available (from previous session or URL)
      const savedTenant = JSON.parse(localStorage.getItem('tenant') || '{}');
      const tenantSlug = savedTenant?.slug || null;
      const response = await authAPI.login({ email, password, tenantSlug });
      const data = response.data.data;
      const { token, user: userData, tenant, subscription } = data;

      // Force-password-change path: server flagged this user (new staff hire
      // whose temp password was emailed, or an admin-initiated reset). Do
      // NOT persist the session yet — hand the temp credentials back so the
      // caller can render the change-password form, then call finalizeLogin
      // once /auth/change-password succeeds. Refreshing mid-flow means the
      // user has to log in again, which is the correct behavior.
      if (data?.must_change_password) {
        return {
          success: true,
          mustChangePassword: true,
          tempToken: token,
          tempUser: userData,
          tempTenant: tenant,
          tempSubscription: subscription,
        };
      }

      // Drop the previous account's onboarding scratchpad if the browser is
      // switching tenants. Without this, user B sees user A's half-filled
      // branches/plan selection carried over.
      try {
        const prevTenant = JSON.parse(localStorage.getItem('tenant') || '{}');
        const prevId = prevTenant?._id || prevTenant?.id;
        const nextId = tenant?._id || tenant?.id;
        if (prevId && nextId && String(prevId) !== String(nextId)) {
          localStorage.removeItem('lis_onboarding_state');
          localStorage.removeItem('lis_onboarding_step');
        }
      } catch { /* ignore */ }

      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(userData));
      if (tenant) localStorage.setItem('tenant', JSON.stringify(tenant));
      if (subscription) localStorage.setItem('subscription', JSON.stringify(subscription));

      setUser(userData);
      setIsAuthenticated(true);

      // Only fetch permissions for activated accounts — see checkAuth above.
      // Calling /roles/my-permissions for a pending tenant triggers the
      // 402 → /subscription-expired loop we just unwound.
      const accountStatus = tenant?.accountStatus || 'active';
      const isActive = accountStatus === 'active';
      if (isActive) {
        await fetchPermissions();
      } else {
        setPermissions({ allowed_pages: [], all_pages: [] });
      }

      // New accounts that haven't gone through checkout yet must finish the
      // onboarding flow before they can see the dashboard. The caller (the
      // Login page) redirects based on this hint.
      //   active            → dashboard (default)
      //   pending_payment   → /onboarding (resumes at the right step)
      //   pending_approval  → /onboarding (lands on step 7)
      //   rejected          → /onboarding (rejected variant of step 7)
      const redirectTo = isActive ? null : '/onboarding';

      return { success: true, accountStatus, redirectTo };
    } catch (error) {
      const message = error.response?.data?.message || 'فشل تسجيل الدخول';
      return { success: false, message };
    }
  };

  // Commit a session that login() deferred because the user was forced to
  // change their password. Called by the Login page after /auth/change-password
  // succeeds using the tempToken. Mirrors the happy-path persistence logic
  // in login() above.
  const finalizeLogin = async ({ token, user: userData, tenant, subscription }) => {
    try {
      const prevTenant = JSON.parse(localStorage.getItem('tenant') || '{}');
      const prevId = prevTenant?._id || prevTenant?.id;
      const nextId = tenant?._id || tenant?.id;
      if (prevId && nextId && String(prevId) !== String(nextId)) {
        localStorage.removeItem('lis_onboarding_state');
        localStorage.removeItem('lis_onboarding_step');
      }
    } catch { /* ignore */ }

    // Clear the must_change_password flag on the cached user — the server
    // already flipped it in the change-password handler.
    const cleanUser = { ...userData, must_change_password: false };

    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(cleanUser));
    if (tenant) localStorage.setItem('tenant', JSON.stringify(tenant));
    if (subscription) localStorage.setItem('subscription', JSON.stringify(subscription));

    setUser(cleanUser);
    setIsAuthenticated(true);

    const accountStatus = tenant?.accountStatus || 'active';
    const isActive = accountStatus === 'active';
    if (isActive) {
      await fetchPermissions();
    } else {
      setPermissions({ allowed_pages: [], all_pages: [] });
    }
    return { accountStatus, redirectTo: isActive ? null : '/onboarding' };
  };

  const logout = useCallback(() => {
    disconnectSocket();
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('selectedBranchId');
    localStorage.removeItem('subscription');
    localStorage.removeItem('tenant');
    // Clear the onboarding scratchpad too — next login will repopulate it
    // from /onboarding/status. Leaving a stale step/state behind makes the
    // next user land in the wrong place if two accounts share a browser.
    localStorage.removeItem('lis_onboarding_state');
    localStorage.removeItem('lis_onboarding_step');
    setUser(null);
    setIsAuthenticated(false);
    setPermissions({ allowed_pages: [], all_pages: [] });
  }, []);

  // Get user initials for avatar
  const getUserInitials = useCallback(() => {
    if (!user?.name) return 'U';
    const parts = user.name.trim().split(' ');
    if (parts.length >= 2) {
      return parts[0].charAt(0) + parts[1].charAt(0);
    }
    return parts[0].charAt(0);
  }, [user]);

  // Check if user can access a specific page
  const canAccessPage = useCallback((pageKey) => {
    // Owner has access to everything
    if (user?.role === 'owner') return true;
    return permissions.allowed_pages.includes(pageKey);
  }, [user, permissions]);

  // Check if user can access a specific path
  const canAccessPath = useCallback((path) => {
    // Owner has access to everything
    if (user?.role === 'owner') return true;
    
    // Find page by path
    const page = permissions.all_pages.find(p => p.path === path);
    if (!page) return true; // If page not in list, allow access (for pages like booking details)
    
    return permissions.allowed_pages.includes(page.key);
  }, [user, permissions]);

  const value = {
    user,
    loading,
    isAuthenticated,
    permissions,
    login,
    finalizeLogin,
    logout,
    checkAuth,
    getUserInitials,
    canAccessPage,
    canAccessPath,
    refreshPermissions: fetchPermissions,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;

